import { createBlocksClient } from "@seliseblocks/client";
import { BLOCKS_API_URL, PROJECT_KEY } from "../../blocks/config";
import apiClient from "../axios";
import type { LeaderboardStandings } from "@/src/models/leaderboard.model";

/**
 * The leaderboard lives in the club-leaderboard Blocks Function: points are derived on the
 * server from real reactions, comments and recorded shares, so clients only read standings
 * and report shares. The leaderboard collection itself is closed to clients.
 */
const leaderboardId = process.env.EXPO_PUBLIC_LEADERBOARD_FUNCTION_ID ?? "";
export const leaderboardEnabled = !!leaderboardId;

// Same transport as the feed Functions: the SDK over the app's axios client (cookie refresh).
const makeClient = () =>
  createBlocksClient({
    apiUrl: BLOCKS_API_URL,
    xBlocksKey: PROJECT_KEY,
    fetch: async (url, init) => {
      const response = await apiClient.request({
        url: String(url),
        method: init?.method,
        data: init?.body,
        headers: Object.fromEntries(new Headers(init?.headers).entries()),
        timeout: 20000,
        validateStatus: (status) => status !== 401,
      });
      return new Response(JSON.stringify(response.data), {
        status: response.status,
        headers: { "content-type": "application/json" },
      });
    },
  });

export class LeaderboardFunctionError extends Error {
  constructor(
    public code: string,
    public status: number,
  ) {
    super(code);
  }
}

const unwrap = (value: any) => {
  if (value && typeof value.statusCode === "number") {
    if (value.statusCode >= 400)
      throw new LeaderboardFunctionError(value.body?.code ?? "LEADERBOARD_UNAVAILABLE", value.statusCode);
    return value.body;
  }
  return value;
};

async function invoke<T>(body: Record<string, unknown>): Promise<T> {
  if (!leaderboardId) throw new LeaderboardFunctionError("LEADERBOARD_NOT_CONFIGURED", 0);
  const blocks = makeClient();
  let value: any;
  try {
    value = await blocks.http.request(`/logic/v4/fn/${encodeURIComponent(leaderboardId)}`, {
      method: "POST",
      body,
      headers: { Prefer: "wait=10" },
    });
  } catch (error: any) {
    if (error?.status && error?.body?.code) throw new LeaderboardFunctionError(error.body.code, error.status);
    throw error;
  }
  // Slow runs answer 202 with a poll token; statuses are uppercase and the result may be a JSON string.
  if (value?.runId && value?.pollToken) {
    const { runId, pollToken } = value;
    value = undefined;
    for (let attempt = 0; attempt < 45; attempt++) {
      await new Promise((resolve) => setTimeout(resolve, 1000));
      const result: any = await blocks.http.request(
        `/logic/v4/fn/runs/${encodeURIComponent(runId)}/result`,
        { headers: { "x-poll-token": pollToken } },
      );
      const status = String(result?.status).toUpperCase();
      if (status === "SUCCEEDED") {
        value = typeof result.result === "string" ? JSON.parse(result.result) : result.result;
        break;
      }
      if (["FAILED", "TIMEDOUT", "TIMED_OUT", "CANCELLED", "CANCELED"].includes(status))
        throw new LeaderboardFunctionError("LEADERBOARD_UNAVAILABLE", 503);
    }
    if (value === undefined) throw new LeaderboardFunctionError("LEADERBOARD_TIMEOUT", 504);
  }
  return unwrap(value) as T;
}

export const fetchStandings = (params: { pageNo?: number; pageSize?: number; period?: string } = {}) =>
  invoke<LeaderboardStandings>({ action: "standings", ...params });

/** Reports a share of a post. Credited at most once per user and post, never for one's own post. */
export const recordShare = (postId: string) =>
  invoke<{ awarded: boolean; reason?: string }>({ action: "share", postId });

/** Account deletion: removes the caller's standings and share records in the active org. */
export const purgeLeaderboard = () => invoke<{ deleted: Record<string, number> }>({ action: "purge" });
